import { useEffect, useRef, useState } from 'react';
import { initTabPresence } from '../../utils/authGuard';

type PresenceState = 'ok' | 'other-tab-detected' | 'locked-out';

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.72)',
  backdropFilter: 'blur(4px)',
  WebkitBackdropFilter: 'blur(4px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 100000,
  padding: '1rem',
};

const cardStyle: React.CSSProperties = {
  maxWidth: '420px',
  width: '100%',
  background: '#ffffff',
  borderRadius: '12px',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  padding: '2rem',
  textAlign: 'center',
  fontFamily: 'inherit',
};

const titleStyle: React.CSSProperties = {
  fontSize: '1.125rem',
  fontWeight: 600,
  color: '#0f172a',
  marginBottom: '0.5rem',
};

const bodyStyle: React.CSSProperties = {
  fontSize: '0.9rem',
  color: '#475569',
  lineHeight: 1.5,
  marginBottom: '1.5rem',
};

const buttonRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: '0.75rem',
  justifyContent: 'center',
  flexWrap: 'wrap',
};

const primaryBtnStyle: React.CSSProperties = {
  padding: '0.6rem 1.25rem',
  background: '#1e40af',
  color: '#ffffff',
  border: 'none',
  borderRadius: '8px',
  fontSize: '0.9rem',
  fontWeight: 500,
  cursor: 'pointer',
};

const secondaryBtnStyle: React.CSSProperties = {
  padding: '0.6rem 1.25rem',
  background: '#f1f5f9',
  color: '#0f172a',
  border: '1px solid #cbd5e1',
  borderRadius: '8px',
  fontSize: '0.9rem',
  fontWeight: 500,
  cursor: 'pointer',
};

/**
 * Blocks multiple simultaneous tabs of the app in the same browser profile.
 *
 * Mount this once inside authenticated layouts. On mount it runs the
 * single-tab presence protocol. If another tab of the app is detected:
 *   - This tab shows a modal offering "Continue here" (take over) or a
 *     dismiss option that reloads the previous tab back in focus.
 *   - If the user picks "Continue here", every other tab receives a takeover
 *     message and shows an immovable lockout screen.
 *
 * Note: `window.close()` only works for scripts-opened tabs, so we don't try
 * to close user-typed URLs — we just instruct the user, or let them take
 * over. The `pathname` gate below skips the guard on the login route so
 * pre-auth double-tabs coexist.
 */
/**
 * Attempt to close the current tab. Browsers only allow window.close() on
 * tabs that were script-opened (window.open, target=_blank from JS). For a
 * user-typed URL or a Ctrl+T tab the call silently fails, so if the page is
 * still here 200ms later we blank it out — at least the app UI is gone and
 * the user can Ctrl+W the empty tab.
 */
const closeThisTab = () => {
  try {
    window.close();
  } catch {
    /* ignore */
  }
  window.setTimeout(() => {
    try {
      window.location.replace('about:blank');
    } catch {
      /* ignore */
    }
  }, 200);
};

export function TabTakeoverGuard() {
  const [state, setState] = useState<PresenceState>('ok');
  const takeOverRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const presence = initTabPresence({
      onExistingTabDetected: () => {
        setState((prev) => (prev === 'ok' ? 'other-tab-detected' : prev));
      },
      onTakeoverReceived: () => {
        setState('locked-out');
      },
    });
    takeOverRef.current = presence.takeOver;
    return presence.stop;
  }, []);

  if (state === 'locked-out') {
    return (
      <div style={overlayStyle} role="dialog" aria-modal="true">
        <div style={cardStyle}>
          <div style={titleStyle}>Session moved to another tab</div>
          <div style={bodyStyle}>
            You opened this application in another tab, so this one has been
            locked to prevent conflicting changes. You can safely close this
            tab.
          </div>
          <div style={buttonRowStyle}>
            <button
              type="button"
              style={secondaryBtnStyle}
              onClick={() => window.location.reload()}
            >
              Reload this tab
            </button>
            <button
              type="button"
              style={secondaryBtnStyle}
              onClick={closeThisTab}
            >
              Close this tab
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (state === 'other-tab-detected') {
    return (
      <div style={overlayStyle} role="dialog" aria-modal="true">
        <div style={cardStyle}>
          <div style={titleStyle}>Already open in another tab</div>
          <div style={bodyStyle}>
            Continue here to take over — the other tab will be locked.
          </div>
          <div style={buttonRowStyle}>
            <button
              type="button"
              style={primaryBtnStyle}
              onClick={() => {
                takeOverRef.current?.();
                setState('ok');
              }}
            >
              Continue here
            </button>
            <button
              type="button"
              style={secondaryBtnStyle}
              onClick={closeThisTab}
            >
              Close this tab
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

export default TabTakeoverGuard;
